import {
  GenerateReportResponseStatusEnum,
  GenerateReportParamsEnum,
  GenerateReportResponse,
  ResponseErrorDto,
} from '@interfaces/models';
import { useCallback, useEffect, useState, useMemo } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useCurrentUser } from '@shared/hooks';
import { useTranslation } from 'react-i18next';
import { downloadFile } from '@helpers/utils';
import * as api from '@api/files-reports';
import { toast } from '@ui/components';
import { DateTime } from 'luxon';

const REPORT_GENERATION_CHECK_INTERVAL = 10000;
const REPORT_GENERATION_TIMEOUT = 15 * 60;

// I don't like the way how it's called on BE, so I'm using alias for it
export enum ReportType {
  COST_CENTERS_SUMMARY = GenerateReportParamsEnum.AllCostCentersSummary,
  EXPENSES_SUMMARY = GenerateReportParamsEnum.AllExpensesSummary,
}

const ReportName: Record<GenerateReportParamsEnum, string> = {
  [ReportType.EXPENSES_SUMMARY]: 'expenses_report',
  [ReportType.COST_CENTERS_SUMMARY]: 'topup_report',
} as const;

export const useGenerateReport = ({
  reportType,
  onDownloadSuccess,
}: {
  onDownloadSuccess: () => void;
  reportType: ReportType;
}) => {
  const { t } = useTranslation();
  const { companyId } = useCurrentUser();
  const [reportStatus, setReportStatus] = useState<GenerateReportResponse | null>(null);

  const dateFrom = DateTime.fromJSDate(new Date(0)).toFormat('yyyy-MM-dd');
  const dateTo = DateTime.now().minus({ days: 1 }).toFormat('yyyy-MM-dd');
  const reportName = ReportName[reportType];
  const filename = `b2b/target/${companyId}/${reportName}_${dateFrom}_${dateTo}.xlsx`;

  const { data: reportGenerationStatus, refetch } = useQuery({
    queryKey: ['report-generation-status', { reportType, dateFrom, dateTo }],
    queryFn: () => api.checkReportGenerationStatus(reportType, dateFrom, dateTo),
    enabled:
      reportStatus?.status === GenerateReportResponseStatusEnum.GENERATING || reportStatus === null,
    refetchInterval: REPORT_GENERATION_CHECK_INTERVAL,
    retry: false,
  });

  const { mutate: generateReport, isPending: isReportGenerating } = useMutation({
    mutationFn: () => api.generateReport(reportType, dateFrom, dateTo),
    onSuccess: () => {
      refetch();
      toast.success(t('FILES_REPORTS.REPORTS.REPORT_GENERATION_STARTED'));
    },
    onError: ({ error }: { error: ResponseErrorDto }) => {
      toast.error(t(`API_ERRORS.${error.errorCode}`));
    },
  });

  const { mutate: getFileFromBucketMutation, isPending: isReportDownloading } = useMutation({
    mutationFn: (fileLink: string) => fetch(fileLink).then((res) => res.blob()),
    onSuccess: (data) => {
      downloadFile(data, filename);
      onDownloadSuccess();
    },
    onError: ({ error }: { error: ResponseErrorDto }) => {
      toast.error(t(`API_ERRORS.${error.errorCode}`));
    },
  });

  const isGenerated = useMemo(() => {
    return (
      reportStatus?.status === GenerateReportResponseStatusEnum.GENERATED && !!reportStatus.link
    );
  }, [reportStatus]);

  const isGenerating = useMemo(() => {
    return reportStatus?.status === GenerateReportResponseStatusEnum.GENERATING;
  }, [reportStatus]);

  const isGeneratingTimeout = useMemo(() => {
    return (
      reportStatus?.status === GenerateReportResponseStatusEnum.GENERATING &&
      DateTime.fromISO(reportStatus.createdAt || '')
        .diffNow()
        .as('seconds') > REPORT_GENERATION_TIMEOUT
    );
  }, [reportStatus]);

  const downloadReport = useCallback(() => {
    if (
      reportStatus?.status === GenerateReportResponseStatusEnum.GENERATED &&
      !!reportStatus.link
    ) {
      getFileFromBucketMutation(reportStatus.link);
    }
  }, [reportStatus, getFileFromBucketMutation]);

  useEffect(() => {
    setReportStatus(reportGenerationStatus || null);
  }, [reportGenerationStatus]);

  return {
    lastGeneratedAt: reportStatus?.createdAt,
    isReportDownloading,
    isGeneratingTimeout,
    isReportGenerating,
    generateReport,
    downloadReport,
    isGenerating,
    isGenerated,
  };
};
